import { useCallback, useEffect, useRef, useState } from "react";
import { Device, type types as MediasoupTypes } from "mediasoup-client";
import type { Socket } from "socket.io-client";
import { createEnhancedVoiceTrack, type EnhancedVoiceTrack, voiceAudioConstraints } from "./voiceProcessing";
import { captureDisplayMedia, isNativeMobileShell, postNativeMessage, prepareNativeCallAudio, releaseNativeCallAudio, releaseNativeScreenCapture } from "./mobileBridge";

export type GroupCallInfo = Readonly<{
  avatarUrl?: string;
  callId: string;
  chatId: string;
  fromUserId: string;
  callerName?: string;
  groupName?: string;
  video: boolean;
  e2eeMode: "required" | "disabled";
  keyEpoch: number;
  participantCount?: number;
  participantLimit?: number;
  nativeCallToken?: string;
  nativeDeclineUrl?: string;
  /** Calculated by the server for the currently authenticated participant. */
  canEnd?: boolean;
  canModerate?: boolean;
  reconnectGraceSeconds?: number;
  startedAt: string;
  status: "incoming" | "joining" | "active";
}>;

export type GroupCallParticipant = Readonly<{
  userId: string;
  displayName: string;
  username: string;
  role: "owner" | "admin" | "member";
  e2eeSupported: boolean;
  connected: boolean;
  handRaised: boolean;
  moderatorMuted: boolean;
  moderatorCameraDisabled: boolean;
  stream: MediaStream;
  screenStream: MediaStream;
  microphone: boolean;
  camera: boolean;
  screen: boolean;
  quality: "checking" | "good" | "fair" | "poor";
}>;

type PeerState = {
  userId: string;
  displayName: string;
  username: string;
  role: "owner" | "admin" | "member";
  e2eeSupported: boolean;
  connected: boolean;
  handRaised: boolean;
  moderatorMuted: boolean;
  moderatorCameraDisabled: boolean;
  stream: MediaStream;
  screenStream: MediaStream;
  microphone: boolean;
  camera: boolean;
  screen: boolean;
  quality: "checking" | "good" | "fair" | "poor";
};

type ProducerSource = "microphone" | "camera" | "screen";
type LocalProducer = MediasoupTypes.Producer<{ source: ProducerSource }>;
type RemoteConsumer = MediasoupTypes.Consumer<{ userId: string; source: ProducerSource; maxSpatialLayer: number }>;
type AckResponse = { error?: { code: string; message: string }; ok?: true; [key: string]: unknown };
type RemoteProducer = { producerId: string; userId: string; source: ProducerSource };
type AdaptiveLayerState = { layer: number; lastChangedAt: number };
type GroupParticipantPublic = {
  userId: string;
  displayName: string;
  username: string;
  role: "owner" | "admin" | "member";
  e2eeSupported: boolean;
  connected: boolean;
  handRaised: boolean;
  moderatorMuted: boolean;
  moderatorCameraDisabled: boolean;
};
export type GroupCallModerationAction = "mute" | "allow_microphone" | "camera_off" | "allow_camera" | "remove" | "mute_all";
export type GroupCallConnectionState = "connected" | "reconnecting" | "failed";

function encodedTransformSupported(): boolean {
  return typeof Worker !== "undefined" && "RTCRtpScriptTransform" in window && "transform" in RTCRtpSender.prototype && "transform" in RTCRtpReceiver.prototype;
}

export async function deriveCallKey(roomKey: Uint8Array, callId: string): Promise<Uint8Array> {
  const keyBytes = new Uint8Array([...roomKey]);
  const material = await crypto.subtle.importKey("raw", keyBytes.buffer, "HKDF", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits({
    name: "HKDF",
    hash: "SHA-256",
    salt: new TextEncoder().encode(callId),
    info: new TextEncoder().encode("JTUC Live media E2EE v1"),
  }, material, 256);
  return new Uint8Array(bits);
}

function emitAck<T extends AckResponse>(socket: Socket, event: string, payload: unknown, timeoutMs = 15_000): Promise<T> {
  return new Promise((resolve, reject) => {
    socket.timeout(timeoutMs).emit(event, payload, (timeout: Error | null, response: T) => {
      if (timeout) { reject(new Error("Сервер групповых звонков не ответил вовремя")); return; }
      if (!response || response.error) { reject(new Error(response?.error?.message ?? "Операция группового звонка отклонена")); return; }
      resolve(response);
    });
  });
}

function videoConstraints(deviceId?: string): MediaTrackConstraints {
  return {
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
    width: { ideal: 1920, max: 1920 }, height: { ideal: 1080, max: 1080 },
    frameRate: { ideal: 60, max: 60 }, facingMode: "user",
  };
}

export function qualityFromScore(score: number): PeerState["quality"] {
  if (score >= 8) return "good";
  if (score >= 5) return "fair";
  return "poor";
}

export function producerScoreValue(scores: readonly { score: number }[]): number | null {
  const valid = scores.map((entry) => entry.score).filter((score) => Number.isFinite(score));
  return valid.length > 0 ? Math.max(...valid) : null;
}

export function preferredGroupVideoCodec(
  codecs: readonly MediasoupTypes.RtpCodecCapability[] | undefined,
): MediasoupTypes.RtpCodecCapability | null {
  return codecs?.find((codec) => codec.kind === "video" && codec.mimeType.toLowerCase() === "video/vp8") ?? null;
}

export function nextAdaptiveLayer(
  score: number,
  maxSpatialLayer: number,
  current: AdaptiveLayerState,
  now: number,
): AdaptiveLayerState {
  const desired = Math.min(maxSpatialLayer, score < 5 ? 0 : score < 8 ? 1 : 2);
  if (desired === current.layer) return current;
  // Degrade relatively quickly, but require a longer stable period before an
  // upgrade. This avoids repeatedly forcing a keyframe on a fluctuating link.
  const holdMs = desired < current.layer ? 2_500 : 8_000;
  return now - current.lastChangedAt >= holdMs ? { layer: desired, lastChangedAt: now } : current;
}

export function useGroupCallController(
  socket: Socket | null,
  getRoomKey: (chatId: string, epoch?: number) => Uint8Array | null,
  ownUserId?: string,
) {
  const [call, setCall] = useState<GroupCallInfo | null>(null);
  const [activeCalls, setActiveCalls] = useState<Record<string, GroupCallInfo>>({});
  const [participants, setParticipants] = useState<GroupCallParticipant[]>([]);
  const [activeSpeakerId, setActiveSpeakerId] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [screenEnabled, setScreenEnabled] = useState(false);
  const [mediaAction, setMediaAction] = useState<ProducerSource | null>(null);
  const [connectionState, setConnectionState] = useState<GroupCallConnectionState>("connected");
  const [handRaised, setHandRaised] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const callRef = useRef<GroupCallInfo | null>(null);
  const pendingNativeAnswerRef = useRef<{ actionId: string | null | undefined; callId: string } | null>(null);
  const pendingNativeDeclineRef = useRef<{ actionId: string | null | undefined; callId: string } | null>(null);
  const nativeAnswerInProgressRef = useRef(false);
  const peersRef = useRef(new Map<string, PeerState>());
  const deviceRef = useRef<Device | null>(null);
  const sendTransportRef = useRef<MediasoupTypes.Transport | null>(null);
  const recvTransportRef = useRef<MediasoupTypes.Transport | null>(null);
  const producersRef = useRef(new Map<ProducerSource, LocalProducer>());
  const consumersRef = useRef(new Map<string, RemoteConsumer>());
  const producerUsersRef = useRef(new Map<string, string>());
  const pendingProducersRef = useRef(new Map<string, RemoteProducer>());
  const consumingProducersRef = useRef(new Set<string>());
  const layerStateRef = useRef(new Map<string, AdaptiveLayerState>());
  const sourceScoresRef = useRef(new Map<string, Partial<Record<ProducerSource, number>>>());
  const mediaWorkerRef = useRef<Worker | null>(null);
  const mediaKeyRef = useRef<Uint8Array | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const voiceProcessorRef = useRef<EnhancedVoiceTrack | null>(null);
  const recoveryTimerRef = useRef<number | null>(null);
  const recoveryAttemptRef = useRef(0);
  const recoveryRunningRef = useRef(false);
  const recoverSessionRef = useRef<() => void>(() => undefined);
  const joinCallRef = useRef<((base: Omit<GroupCallInfo, "status">, withVideo: boolean) => Promise<void>) | null>(null);
  const ringtoneTimerRef = useRef<number | null>(null);
  const cameraDeviceIdRef = useRef<string | undefined>(undefined);
  const cameraWantedRef = useRef(false);
  const cameraMutedAtRef = useRef<number | null>(null);
  const cameraRecoveryRunningRef = useRef(false);
  const screenStoppingRef = useRef(false);

  const updateCall = useCallback((value: GroupCallInfo | null) => {
    callRef.current = value;
    setCall(value);
  }, []);

  const renderPeers = useCallback(() => {
    setParticipants([...peersRef.current.values()].map((peer) => ({ ...peer })));
  }, []);

  const updatePeerQuality = useCallback((userId: string, source: ProducerSource, score: number) => {
    const sources = sourceScoresRef.current.get(userId) ?? {};
    sources[source] = score;
    sourceScoresRef.current.set(userId, sources);
    const values = Object.values(sources).filter((value): value is number => typeof value === "number");
    const effective = sources.microphone ?? (values.length > 0 ? Math.min(...values) : null);
    const peer = peersRef.current.get(userId);
    if (peer && effective !== null) {
      peer.quality = qualityFromScore(effective);
      renderPeers();
    }
  }, [renderPeers]);

  const stopRingtone = useCallback(() => {
    if (ringtoneTimerRef.current !== null) window.clearInterval(ringtoneTimerRef.current);
    ringtoneTimerRef.current = null;
  }, []);

  const playRingPulse = useCallback(() => {
    try {
      const AudioContextClass = window.AudioContext;
      const context = new AudioContextClass();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.setValueAtTime(740, context.currentTime);
      gain.gain.setValueAtTime(0.0001, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.45);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(); oscillator.stop(context.currentTime + 0.48);
      oscillator.addEventListener("ended", () => void context.close());
    } catch { /* Autoplay restrictions are expected before a user gesture. */ }
  }, []);

  const startRingtone = useCallback(() => {
    stopRingtone(); playRingPulse();
    ringtoneTimerRef.current = window.setInterval(playRingPulse, 2_200);
  }, [playRingPulse, stopRingtone]);

  const attachTransform = useCallback((target: RTCRtpSender | RTCRtpReceiver, operation: "encrypt" | "decrypt") => {
    const key = mediaKeyRef.current;
    const worker = mediaWorkerRef.current;
    if (!key || !worker || !encodedTransformSupported()) return;
    const TransformConstructor = (window as unknown as { RTCRtpScriptTransform: new (worker: Worker, options: object) => unknown }).RTCRtpScriptTransform;
    (target as unknown as { transform: unknown }).transform = new TransformConstructor(worker, { operation, key: [...key] });
  }, []);

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    setDevices(await navigator.mediaDevices.enumerateDevices());
  }, []);

  const cleanup = useCallback(() => {
    const endingCall = callRef.current;
    if (endingCall) postNativeMessage({ type: "call:ended", callId: endingCall.callId });
    releaseNativeCallAudio();
    releaseNativeScreenCapture();
    stopRingtone();
    if (recoveryTimerRef.current !== null) window.clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = null; recoveryAttemptRef.current = 0; recoveryRunningRef.current = false;
    for (const consumer of consumersRef.current.values()) consumer.close();
    for (const producer of producersRef.current.values()) { producer.track?.stop(); producer.close(); }
    consumersRef.current.clear(); producersRef.current.clear(); producerUsersRef.current.clear();
    pendingProducersRef.current.clear(); consumingProducersRef.current.clear(); layerStateRef.current.clear(); sourceScoresRef.current.clear();
    sendTransportRef.current?.close(); recvTransportRef.current?.close();
    sendTransportRef.current = null; recvTransportRef.current = null; deviceRef.current = null;
    localStreamRef.current?.getTracks().forEach((track) => track.stop()); localStreamRef.current = null;
    if (voiceProcessorRef.current) void voiceProcessorRef.current.dispose();
    voiceProcessorRef.current = null;
    setLocalStream(null);
    for (const peer of peersRef.current.values()) {
      peer.stream.getTracks().forEach((track) => track.stop());
      peer.screenStream.getTracks().forEach((track) => track.stop());
    }
    peersRef.current.clear(); renderPeers();
    mediaWorkerRef.current?.terminate(); mediaWorkerRef.current = null; mediaKeyRef.current = null;
    cameraWantedRef.current = false; cameraDeviceIdRef.current = undefined; cameraMutedAtRef.current = null; cameraRecoveryRunningRef.current = false;
    screenStoppingRef.current = false;
    setMuted(false); setCameraEnabled(false); setScreenEnabled(false); setMediaAction(null); setActiveSpeakerId(null); setHandRaised(false); setConnectionState("connected");
    updateCall(null);
  }, [renderPeers, stopRingtone, updateCall]);

  const scheduleRecovery = useCallback((immediate = false) => {
    if (!callRef.current || recoveryTimerRef.current !== null || recoveryRunningRef.current) return;
    setConnectionState("reconnecting");
    setError("Связь прервалась — восстанавливаем звонок автоматически…");
    const delay = immediate ? 0 : Math.min(8_000, 1_000 * (2 ** Math.min(3, recoveryAttemptRef.current)));
    recoveryTimerRef.current = window.setTimeout(() => {
      recoveryTimerRef.current = null;
      recoverSessionRef.current();
    }, delay);
  }, []);

  const createTransport = useCallback(async (callId: string, direction: "send" | "recv", device: Device) => {
    if (!socket) throw new Error("Нет соединения с сервером");
    const response = await emitAck<AckResponse & { transport: MediasoupTypes.TransportOptions }>(socket, "group-call:create-transport", { callId, direction });
    const transport = direction === "send" ? device.createSendTransport(response.transport) : device.createRecvTransport(response.transport);
    transport.on("connect", ({ dtlsParameters }, callback, errback) => {
      void emitAck(socket, "group-call:connect-transport", { callId, transportId: transport.id, dtlsParameters }).then(() => callback()).catch(errback);
    });
    transport.on("connectionstatechange", (state) => {
      if (state === "connected") {
        setConnectionState("connected");
        recoveryAttemptRef.current = 0;
        setError((current) => current?.startsWith("Связь прервалась") ? null : current);
      }
      if (state === "failed" || state === "disconnected") scheduleRecovery(state === "failed");
    });
    if (direction === "send") {
      transport.on("produce", ({ kind, rtpParameters, appData }, callback, errback) => {
        const source = (appData as { source: ProducerSource }).source;
        void emitAck<AckResponse & { producerId: string }>(socket, "group-call:produce", { callId, transportId: transport.id, kind, rtpParameters, source })
          .then((result) => callback({ id: result.producerId })).catch(errback);
      });
    }
    return transport;
  }, [scheduleRecovery, socket]);

  const consumeProducer = useCallback(async (raw: RemoteProducer) => {
    const info = callRef.current; const device = deviceRef.current; const transport = recvTransportRef.current;
    if (!socket || !info || !device || !transport || !peersRef.current.has(raw.userId)) {
      pendingProducersRef.current.set(raw.producerId, raw);
      return;
    }
    if (consumersRef.current.has(raw.producerId) || consumingProducersRef.current.has(raw.producerId)) return;
    consumingProducersRef.current.add(raw.producerId);
    try {
      const response = await emitAck<AckResponse & { consumer: { id: string; producerId: string; userId: string; source: ProducerSource; kind: "audio" | "video"; rtpParameters: MediasoupTypes.RtpParameters; producerPaused: boolean; maxSpatialLayer: number } }>(socket, "group-call:consume", {
        callId: info.callId, transportId: transport.id, producerId: raw.producerId, rtpCapabilities: device.rtpCapabilities,
      });
      const data = response.consumer;
      const consumer = await transport.consume<{ userId: string; source: ProducerSource; maxSpatialLayer: number }>({
        id: data.id, producerId: data.producerId, kind: data.kind, rtpParameters: data.rtpParameters,
        appData: { userId: data.userId, source: data.source, maxSpatialLayer: data.maxSpatialLayer },
        onRtpReceiver: (receiver) => attachTransform(receiver, "decrypt"),
      });
      consumersRef.current.set(data.producerId, consumer);
      producerUsersRef.current.set(data.producerId, data.userId);
      layerStateRef.current.set(data.producerId, { layer: data.maxSpatialLayer, lastChangedAt: Date.now() });
      pendingProducersRef.current.delete(data.producerId);
      const peer = peersRef.current.get(data.userId);
      if (peer) {
        const target = data.source === "screen" ? peer.screenStream : peer.stream;
        target.addTrack(consumer.track);
        if (data.source === "microphone") peer.microphone = !data.producerPaused;
        if (data.source === "camera") peer.camera = !data.producerPaused;
        if (data.source === "screen") peer.screen = !data.producerPaused;
        renderPeers();
      }
      consumer.on("trackended", () => {
        const current = peersRef.current.get(data.userId);
        if (current) {
          current.stream.removeTrack(consumer.track);
          current.screenStream.removeTrack(consumer.track);
          if (data.source === "microphone") current.microphone = false;
          if (data.source === "camera") current.camera = false;
          if (data.source === "screen") current.screen = false;
          renderPeers();
        }
      });
      await emitAck(socket, "group-call:resume-consumer", { callId: info.callId, consumerId: consumer.id });
    } finally {
      consumingProducersRef.current.delete(raw.producerId);
    }
  }, [attachTransform, renderPeers, socket]);

  const produceTrack = useCallback(async (track: MediaStreamTrack, source: ProducerSource): Promise<LocalProducer> => {
    const transport = sendTransportRef.current;
    if (!transport) throw new Error("Транспорт отправки не готов");
    const videoCodec = source === "microphone"
      ? null
      : preferredGroupVideoCodec(deviceRef.current?.rtpCapabilities.codecs);
    if (source !== "microphone" && !videoCodec) {
      throw new Error("Браузер не поддерживает VP8, необходимый для защищённого группового видео");
    }
    const options: MediasoupTypes.ProducerOptions<{ source: ProducerSource }> = {
      track, stopTracks: false, appData: { source }, onRtpSender: (sender) => attachTransform(sender, "encrypt"),
      ...(videoCodec ? { codec: videoCodec } : {}),
      ...(source === "microphone" ? { codecOptions: { opusStereo: false, opusFec: true, opusDtx: true, opusNack: true, opusMaxAverageBitrate: 96_000, opusPtime: 20 } } : {}),
      ...(source === "camera" ? { encodings: [
        { rid: "q", maxBitrate: 180_000, maxFramerate: 20, scaleResolutionDownBy: 4, scalabilityMode: "S1T3" },
        { rid: "h", maxBitrate: 750_000, maxFramerate: 30, scaleResolutionDownBy: 2, scalabilityMode: "S1T3" },
        { rid: "f", maxBitrate: 4_500_000, maxFramerate: 60, scaleResolutionDownBy: 1, scalabilityMode: "S1T3" },
      ], codecOptions: { videoGoogleStartBitrate: 1_500, videoGoogleMinBitrate: 100, videoGoogleMaxBitrate: 5_000 } } : {}),
      ...(source === "screen" ? { encodings: [{ maxBitrate: 6_000_000, maxFramerate: 30 }], codecOptions: { videoGoogleStartBitrate: 2_500, videoGoogleMaxBitrate: 7_000 } } : {}),
    };
    const producer = await transport.produce(options) as LocalProducer;
    producersRef.current.set(source, producer);
    return producer;
  }, [attachTransform]);

  const replaceLocalTrack = useCallback((previous: MediaStreamTrack | null, next: MediaStreamTrack) => {
    const stream = localStreamRef.current ?? new MediaStream();
    if (previous) stream.removeTrack(previous);
    stream.addTrack(next);
    const refreshed = new MediaStream(stream.getTracks());
    localStreamRef.current = refreshed;
    setLocalStream(refreshed);
  }, []);

  const acquireCameraTrack = useCallback(async (deviceId?: string): Promise<MediaStreamTrack> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints(deviceId) });
      const track = stream.getVideoTracks()[0];
      if (!track) throw new Error("Камера не вернула видеопоток");
      return track;
    } catch (error) {
      if (!deviceId) throw error;
      const fallback = await navigator.mediaDevices.getUserMedia({ video: videoConstraints() });
      const track = fallback.getVideoTracks()[0];
      if (!track) throw new Error("Камера не вернула видеопоток");
      return track;
    }
  }, []);

  const recoverCameraTrack = useCallback(async () => {
    if (!cameraWantedRef.current || cameraRecoveryRunningRef.current) return;
    const producer = producersRef.current.get("camera");
    if (!producer || producer.closed || producer.paused) return;
    cameraRecoveryRunningRef.current = true;
    try {
      const previous = producer.track;
      const replacement = await acquireCameraTrack(cameraDeviceIdRef.current);
      await producer.replaceTrack({ track: replacement });
      replaceLocalTrack(previous, replacement);
      cameraDeviceIdRef.current = replacement.getSettings().deviceId;
      previous?.stop();
      cameraMutedAtRef.current = null;
    } catch (caught) {
      setError(caught instanceof Error ? `Не удалось восстановить камеру: ${caught.message}` : "Не удалось восстановить камеру");
    } finally {
      cameraRecoveryRunningRef.current = false;
    }
  }, [acquireCameraTrack, replaceLocalTrack]);

  const joinCall = useCallback(async (base: Omit<GroupCallInfo, "status">, withVideo: boolean) => {
    if (!socket?.connected) throw new Error("Нет соединения с сервером");
    stopRingtone(); setError(null); updateCall({ ...base, status: "joining" });
    const e2eeSupported = encodedTransformSupported();
    const roomKey = getRoomKey(base.chatId, base.keyEpoch);
    if (base.e2eeMode === "required" && (!e2eeSupported || !roomKey)) throw new Error("На этом устройстве нет совместимой поддержки защищённого медиапотока или ключа группы");
    if (base.e2eeMode === "required" && roomKey) {
      mediaKeyRef.current = await deriveCallKey(roomKey, base.callId);
      const mediaWorker = new Worker("/media-e2ee-worker.js?v=2");
      mediaWorker.addEventListener("message", (event: MessageEvent<unknown>) => {
        const message = event.data as { type?: string; operation?: string } | null;
        if (message?.type === "media-e2ee-error" && message.operation === "decrypt") {
          setError("Не удалось расшифровать медиапоток участника. Обновите страницу на всех устройствах звонка.");
        }
      });
      mediaWorkerRef.current = mediaWorker;
    }
    await prepareNativeCallAudio(withVideo);
    const captured = await navigator.mediaDevices.getUserMedia({ audio: voiceAudioConstraints(), video: withVideo ? videoConstraints() : false });
    const rawMicrophone = captured.getAudioTracks()[0];
    if (!rawMicrophone) throw new Error("Микрофон не вернул звуковой поток");
    const voiceProcessor = await createEnhancedVoiceTrack(rawMicrophone);
    voiceProcessorRef.current = voiceProcessor;
    const media = new MediaStream([voiceProcessor.track, ...captured.getVideoTracks()]);
    localStreamRef.current = media;
    setLocalStream(media);
    await refreshDevices();
    const joined = await emitAck<AckResponse & { call: Omit<GroupCallInfo, "status">; routerRtpCapabilities: MediasoupTypes.RtpCapabilities; participants: GroupParticipantPublic[]; producers: Array<{ producerId: string; userId: string; source: ProducerSource }> }>(socket, "group-call:join", { callId: base.callId, e2eeSupported });
    const device = await Device.factory(); await device.load({ routerRtpCapabilities: joined.routerRtpCapabilities }); deviceRef.current = device;
    sendTransportRef.current = await createTransport(base.callId, "send", device);
    recvTransportRef.current = await createTransport(base.callId, "recv", device);
    peersRef.current.clear();
    for (const participant of joined.participants) peersRef.current.set(participant.userId, { ...participant, stream: new MediaStream(), screenStream: new MediaStream(), microphone: false, camera: false, screen: false, quality: "checking" });
    if (ownUserId) setHandRaised(peersRef.current.get(ownUserId)?.handRaised ?? false);
    renderPeers();
    const microphone = media.getAudioTracks()[0];
    if (microphone) {
      await produceTrack(microphone, "microphone");
      const own = ownUserId ? peersRef.current.get(ownUserId) : undefined;
      if (own) own.microphone = true;
    }
    const camera = media.getVideoTracks()[0];
    if (camera) {
      await produceTrack(camera, "camera");
      cameraDeviceIdRef.current = camera.getSettings().deviceId;
      cameraWantedRef.current = true;
      const own = ownUserId ? peersRef.current.get(ownUserId) : undefined;
      if (own) own.camera = true;
      setCameraEnabled(true);
    }
    renderPeers();
    const available = new Map<string, RemoteProducer>();
    for (const producer of joined.producers) available.set(producer.producerId, producer);
    for (const producer of pendingProducersRef.current.values()) available.set(producer.producerId, producer);
    for (const producer of available.values()) await consumeProducer(producer);
    updateCall({ ...joined.call, status: "active" });
    setConnectionState("connected"); recoveryAttemptRef.current = 0;
    setActiveCalls((current) => ({ ...current, [base.chatId]: { ...joined.call, status: "active" } }));
  }, [consumeProducer, createTransport, getRoomKey, ownUserId, produceTrack, refreshDevices, renderPeers, socket, stopRingtone, updateCall]);

  joinCallRef.current = joinCall;

  const recoverSession = useCallback(async () => {
    const current = callRef.current;
    if (!current || current.status !== "active" || recoveryRunningRef.current) return;
    if (!socket?.connected) { scheduleRecovery(); return; }
    recoveryRunningRef.current = true;
    let retry = false;
    try {
      const resumed = await emitAck<AckResponse & { call: Omit<GroupCallInfo, "status"> }>(
        socket,
        "group-call:resume-session",
        { callId: current.callId },
        8_000,
      );
      updateCall({ ...current, ...resumed.call, status: "active" });
      for (const transport of [sendTransportRef.current, recvTransportRef.current]) {
        if (!transport || transport.closed) continue;
        const response = await emitAck<AckResponse & { iceParameters: MediasoupTypes.IceParameters }>(
          socket,
          "group-call:restart-ice",
          { callId: current.callId, transportId: transport.id },
          8_000,
        );
        await transport.restartIce({ iceParameters: response.iceParameters });
      }
      recoveryAttemptRef.current = 0;
      setConnectionState("connected");
      setError(null);
    } catch (caught) {
      recoveryAttemptRef.current += 1;
      if (recoveryAttemptRef.current >= 5) {
        const snapshot = { ...current };
        const withVideo = cameraWantedRef.current;
        try {
          await emitAck(socket, "group-call:leave", { callId: current.callId }, 5_000).catch(() => undefined);
          cleanup();
          await joinCallRef.current?.(snapshot, withVideo);
          return;
        } catch (rejoinError) {
          setConnectionState("failed");
          setError(rejoinError instanceof Error ? `Не удалось восстановить звонок: ${rejoinError.message}` : "Не удалось восстановить звонок");
          return;
        }
      }
      retry = true;
      setError(caught instanceof Error ? `Восстанавливаем звонок: ${caught.message}` : "Восстанавливаем звонок…");
    } finally {
      recoveryRunningRef.current = false;
      if (retry) scheduleRecovery();
    }
  }, [cleanup, scheduleRecovery, socket, updateCall]);

  recoverSessionRef.current = () => void recoverSession();

  const startCall = useCallback(async (chatId: string, video: boolean) => {
    if (!socket?.connected || callRef.current) return;
    try {
      const supports = encodedTransformSupported(); const key = getRoomKey(chatId);
      if (!key) throw new Error("Сначала получите ключ текущей эпохи группы");
      const response = await emitAck<AckResponse & { callId: string; e2eeMode: "required" | "disabled"; keyEpoch: number }>(socket, "group-call:start", { chatId, video, e2eeSupported: supports, e2eeMode: supports && key ? "required" : "disabled" });
      await joinCall({ callId: response.callId, chatId, fromUserId: "", video, e2eeMode: response.e2eeMode, keyEpoch: response.keyEpoch, startedAt: new Date().toISOString() }, video);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Не удалось начать групповой звонок"); cleanup(); }
  }, [cleanup, getRoomKey, joinCall, socket]);

  const acceptCall = useCallback(async (): Promise<boolean> => {
    const current = callRef.current; if (!current || current.status !== "incoming") return false;
    try { await joinCall(current, current.video); return true; } catch (caught) { setError(caught instanceof Error ? caught.message : "Не удалось войти в звонок"); cleanup(); return false; }
  }, [cleanup, joinCall]);

  const processPendingNativeAnswer = useCallback(() => {
    const intent = pendingNativeAnswerRef.current;
    const current = callRef.current;
    if (!intent || !current || current.callId !== intent.callId || !socket?.connected) return;
    if (current.status === "active") {
      pendingNativeAnswerRef.current = null;
      if (intent.actionId) postNativeMessage({ type: "call:action-ack", actionId: intent.actionId });
      return;
    }
    if (current.status !== "incoming" || nativeAnswerInProgressRef.current) return;
    if (current.e2eeMode === "required" && getRoomKey(current.chatId, current.keyEpoch) === null) return;
    nativeAnswerInProgressRef.current = true;
    void acceptCall()
      .then((processed) => {
        if (!processed) return;
        const latest = pendingNativeAnswerRef.current;
        if (latest?.callId !== intent.callId) return;
        pendingNativeAnswerRef.current = null;
        if (latest.actionId) postNativeMessage({ type: "call:action-ack", actionId: latest.actionId });
      })
      .finally(() => { nativeAnswerInProgressRef.current = false; });
  }, [acceptCall, getRoomKey, socket]);

  const joinActiveCall = useCallback(async (chatId: string) => {
    const active = activeCalls[chatId];
    if (!active || callRef.current) return;
    try { await joinCall(active, false); } catch (caught) { setError(caught instanceof Error ? caught.message : "Не удалось войти в звонок"); cleanup(); }
  }, [activeCalls, cleanup, joinCall]);

  const declineCall = useCallback(() => {
    const current = callRef.current;
    if (current) postNativeMessage({ type: "call:ended", callId: current.callId });
    stopRingtone();
    updateCall(null);
  }, [stopRingtone, updateCall]);
  const leaveCall = useCallback(() => { const current = callRef.current; if (current && socket?.connected) socket.emit("group-call:leave", { callId: current.callId }); cleanup(); }, [cleanup, socket]);
  const endCall = useCallback(() => { const current = callRef.current; if (current && socket?.connected) socket.emit("group-call:end", { callId: current.callId }); cleanup(); }, [cleanup, socket]);

  const toggleHandRaised = useCallback(async () => {
    const current = callRef.current;
    if (!current || !socket?.connected) return;
    const raised = !handRaised;
    await emitAck(socket, "group-call:set-hand-raised", { callId: current.callId, raised });
    setHandRaised(raised);
  }, [handRaised, socket]);

  const moderateParticipant = useCallback(async (action: GroupCallModerationAction, targetUserId?: string) => {
    const current = callRef.current;
    if (!current || !socket?.connected || !current.canModerate) return;
    setError(null);
    try {
      await emitAck(socket, "group-call:moderate", { callId: current.callId, action, targetUserId });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось выполнить действие модератора");
    }
  }, [socket]);

  const toggleMute = useCallback(async () => {
    const producer = producersRef.current.get("microphone"); const current = callRef.current;
    if (!producer || !socket || !current) return;
    const paused = !muted;
    setMediaAction("microphone"); setError(null);
    try {
      if (paused && !producer.paused) producer.pause();
      if (!paused && producer.paused) producer.resume();
      if (producer.track) producer.track.enabled = !paused;
      setMuted(paused);
      const own = ownUserId ? peersRef.current.get(ownUserId) : undefined;
      if (own) { own.microphone = !paused; renderPeers(); }
      await emitAck(socket, "group-call:set-producer-paused", { callId: current.callId, producerId: producer.id, paused });
    } catch (caught) {
      if (muted && !producer.paused) producer.pause();
      if (!muted && producer.paused) producer.resume();
      if (producer.track) producer.track.enabled = !muted;
      setMuted(muted);
      const own = ownUserId ? peersRef.current.get(ownUserId) : undefined;
      if (own) { own.microphone = !muted; renderPeers(); }
      setError(caught instanceof Error ? caught.message : "Не удалось переключить микрофон");
    } finally {
      setMediaAction(null);
    }
  }, [muted, ownUserId, renderPeers, socket]);

  const toggleCamera = useCallback(async () => {
    const current = callRef.current; if (!current || !socket) return;
    setMediaAction("camera"); setError(null);
    const producer = producersRef.current.get("camera");
    const enable = !cameraEnabled;
    try {
      if (producer) {
        if (enable && producer.paused) producer.resume();
        if (!enable && !producer.paused) producer.pause();
        if (producer.track) producer.track.enabled = enable;
        await emitAck(socket, "group-call:set-producer-paused", { callId: current.callId, producerId: producer.id, paused: !enable });
      } else {
        if (!enable) return;
        const track = await acquireCameraTrack(cameraDeviceIdRef.current);
        try {
          await produceTrack(track, "camera");
        } catch (error) {
          track.stop();
          throw error;
        }
        replaceLocalTrack(null, track);
        cameraDeviceIdRef.current = track.getSettings().deviceId;
        await refreshDevices();
      }
      cameraWantedRef.current = enable;
      const own = ownUserId ? peersRef.current.get(ownUserId) : undefined;
      if (own) { own.camera = enable; renderPeers(); }
      setCameraEnabled(enable);
    } catch (caught) {
      if (producer) {
        if (cameraEnabled && producer.paused) producer.resume();
        if (!cameraEnabled && !producer.paused) producer.pause();
        if (producer.track) producer.track.enabled = cameraEnabled;
      }
      setError(caught instanceof Error ? caught.message : "Не удалось переключить камеру");
    } finally {
      setMediaAction(null);
    }
  }, [acquireCameraTrack, cameraEnabled, ownUserId, produceTrack, refreshDevices, renderPeers, replaceLocalTrack, socket]);

  const stopScreen = useCallback(async () => {
    if (screenStoppingRef.current) return;
    screenStoppingRef.current = true;
    const producer = producersRef.current.get("screen"); const current = callRef.current;
    try {
      if (producer && current && socket) { await emitAck(socket, "group-call:close-producer", { callId: current.callId, producerId: producer.id }).catch(() => undefined); producer.track?.stop(); producer.close(); }
      producersRef.current.delete("screen");
      releaseNativeScreenCapture();
      const own = ownUserId ? peersRef.current.get(ownUserId) : undefined;
      if (own) {
        own.screenStream.getTracks().forEach((track) => own.screenStream.removeTrack(track));
        own.screen = false;
        renderPeers();
      }
      setScreenEnabled(false);
    } finally {
      screenStoppingRef.current = false;
    }
  }, [ownUserId, renderPeers, socket]);

  const toggleScreen = useCallback(async () => {
    setMediaAction("screen"); setError(null);
    try {
      if (screenEnabled) { await stopScreen(); return; }
      const stream = await captureDisplayMedia();
      const track = stream.getVideoTracks()[0]; if (!track) return;
      try {
        await produceTrack(track, "screen");
      } catch (error) {
        track.stop();
        throw error;
      }
      const own = ownUserId ? peersRef.current.get(ownUserId) : undefined;
      if (own) { own.screenStream.addTrack(track); own.screen = true; renderPeers(); }
      track.addEventListener("ended", () => void stopScreen(), { once: true });
      setScreenEnabled(true);
    } catch (caught) {
      const name = caught instanceof DOMException ? caught.name : "";
      if (name !== "NotAllowedError") setError(caught instanceof Error ? caught.message : "Не удалось включить демонстрацию экрана");
    } finally {
      setMediaAction(null);
    }
  }, [ownUserId, produceTrack, renderPeers, screenEnabled, stopScreen]);

  const switchDevice = useCallback(async (kind: "audioinput" | "videoinput", deviceId: string) => {
    const source: ProducerSource = kind === "audioinput" ? "microphone" : "camera";
    const producer = producersRef.current.get(source);
    const stream = await navigator.mediaDevices.getUserMedia(kind === "audioinput" ? { audio: voiceAudioConstraints(deviceId) } : { video: videoConstraints(deviceId) });
    const rawTrack = kind === "audioinput" ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
    if (!rawTrack) return;
    const nextVoiceProcessor = kind === "audioinput" ? await createEnhancedVoiceTrack(rawTrack) : null;
    const track = nextVoiceProcessor?.track ?? rawTrack;
    if (!track) return;
    track.enabled = kind === "audioinput" ? !muted : cameraEnabled;
    if (producer) {
      const previous = producer.track;
      await producer.replaceTrack({ track });
      replaceLocalTrack(previous, track);
      if (kind === "audioinput") {
        const previousProcessor = voiceProcessorRef.current;
        voiceProcessorRef.current = nextVoiceProcessor;
        if (previousProcessor) await previousProcessor.dispose(); else previous?.stop();
      } else previous?.stop();
    } else {
      replaceLocalTrack(null, track);
      await produceTrack(track, source);
      if (kind === "audioinput") voiceProcessorRef.current = nextVoiceProcessor;
      if (kind === "videoinput") setCameraEnabled(true);
    }
    if (kind === "videoinput") {
      cameraDeviceIdRef.current = track.getSettings().deviceId;
      cameraWantedRef.current = true;
    }
  }, [cameraEnabled, muted, produceTrack, replaceLocalTrack]);

  useEffect(() => {
    if (!socket) return;
    const incoming = (value: Omit<GroupCallInfo, "status">) => {
      setActiveCalls((current) => ({ ...current, [value.chatId]: { ...value, status: "incoming" } }));
      if (!callRef.current) {
        updateCall({ ...value, status: "incoming" });
        if (!isNativeMobileShell()) startRingtone();
      }
      postNativeMessage({
        type: "call:incoming",
        callId: value.callId,
        chatId: value.chatId,
        fromUserId: value.fromUserId,
        callerName: value.callerName ?? "Участник",
        group: true,
        groupName: value.groupName ?? "Групповой чат",
        video: value.video,
        e2eeMode: value.e2eeMode,
        keyEpoch: value.keyEpoch,
        startedAt: value.startedAt,
        avatarUrl: value.avatarUrl,
        nativeCallToken: value.nativeCallToken,
        nativeDeclineUrl: value.nativeDeclineUrl,
      });
    };
    const active = (value: Omit<GroupCallInfo, "status">) => setActiveCalls((current) => ({ ...current, [value.chatId]: { ...value, status: "active" } }));
    const inactive = (value: { chatId: string }) => setActiveCalls((current) => { const next = { ...current }; delete next[value.chatId]; return next; });
    const upsertParticipant = (value: GroupParticipantPublic) => {
      const previous = peersRef.current.get(value.userId);
      peersRef.current.set(value.userId, {
        ...value,
        stream: previous?.stream ?? new MediaStream(),
        screenStream: previous?.screenStream ?? new MediaStream(),
        microphone: previous?.microphone ?? false,
        camera: previous?.camera ?? false,
        screen: previous?.screen ?? false,
        quality: previous?.quality ?? "checking",
      });
      if (value.userId === ownUserId) setHandRaised(value.handRaised);
      renderPeers();
    };
    const joined = (value: GroupParticipantPublic) => {
      upsertParticipant(value);
      for (const producer of pendingProducersRef.current.values()) {
        if (producer.userId === value.userId) void consumeProducer(producer).catch((caught) => setError(caught instanceof Error ? caught.message : "Не удалось получить медиапоток"));
      }
    };
    const participantUpdated = (value: GroupParticipantPublic) => upsertParticipant(value);
    const left = (value: { userId: string }) => {
      peersRef.current.delete(value.userId); sourceScoresRef.current.delete(value.userId);
      for (const [producerId, producer] of pendingProducersRef.current) if (producer.userId === value.userId) pendingProducersRef.current.delete(producerId);
      renderPeers();
    };
    const producerAdded = (value: RemoteProducer) => void consumeProducer(value).catch((caught) => setError(caught instanceof Error ? caught.message : "Не удалось получить медиапоток"));
    const producerClosed = (value: { producerId: string }) => {
      const consumer = consumersRef.current.get(value.producerId); const userId = producerUsersRef.current.get(value.producerId);
      if (consumer) { consumer.close(); consumersRef.current.delete(value.producerId); }
      producerUsersRef.current.delete(value.producerId); pendingProducersRef.current.delete(value.producerId); layerStateRef.current.delete(value.producerId);
      if (userId) {
        const peer = peersRef.current.get(userId);
        if (peer && consumer) {
          peer.stream.removeTrack(consumer.track); peer.screenStream.removeTrack(consumer.track);
          if (consumer.appData.source === "microphone") peer.microphone = false;
          if (consumer.appData.source === "camera") peer.camera = false;
          if (consumer.appData.source === "screen") peer.screen = false;
          renderPeers();
        }
      }
    };
    const producerActivity = (active: boolean) => (value: { producerId: string }) => { const consumer = consumersRef.current.get(value.producerId); const userId = producerUsersRef.current.get(value.producerId); const peer = userId ? peersRef.current.get(userId) : undefined; if (!consumer || !peer) return; if (consumer.appData.source === "microphone") peer.microphone = active; if (consumer.appData.source === "camera") peer.camera = active; if (consumer.appData.source === "screen") peer.screen = active; renderPeers(); };
    const producerPaused = producerActivity(false); const producerResumed = producerActivity(true);
    const activeSpeaker = (value: { userId: string | null }) => setActiveSpeakerId(value.userId);
    const consumerScore = (value: { producerId: string; consumerId: string; score: { score: number } }) => {
      const userId = producerUsersRef.current.get(value.producerId);
      const consumer = consumersRef.current.get(value.producerId);
      if (userId && consumer) updatePeerQuality(userId, consumer.appData.source, value.score.score);
      const currentCall = callRef.current;
      if (!currentCall || consumer?.kind !== "video" || consumer.appData.maxSpatialLayer < 1 || !socket.connected) return;
      const currentLayer = layerStateRef.current.get(value.producerId) ?? { layer: consumer.appData.maxSpatialLayer, lastChangedAt: Date.now() };
      const nextLayer = nextAdaptiveLayer(value.score.score, consumer.appData.maxSpatialLayer, currentLayer, Date.now());
      layerStateRef.current.set(value.producerId, nextLayer);
      if (nextLayer.layer !== currentLayer.layer) {
        socket.emit("group-call:set-layers", { callId: currentCall.callId, consumerId: value.consumerId, spatialLayer: nextLayer.layer, temporalLayer: 2 });
      }
    };
    const producerScore = (value: { userId: string; source: ProducerSource; score: Array<{ score: number }> }) => {
      const score = producerScoreValue(value.score);
      if (score !== null) updatePeerQuality(value.userId, value.source, score);
    };
    const moderated = (value: { action: GroupCallModerationAction; targetUserId: string }) => {
      if (value.targetUserId !== ownUserId) return;
      const own = ownUserId ? peersRef.current.get(ownUserId) : undefined;
      if (value.action === "mute") {
        const producer = producersRef.current.get("microphone");
        if (producer && !producer.paused) producer.pause();
        if (producer?.track) producer.track.enabled = false;
        setMuted(true);
        if (own) own.microphone = false;
        setError("Модератор выключил ваш микрофон");
      } else if (value.action === "camera_off") {
        const producer = producersRef.current.get("camera");
        if (producer && !producer.paused) producer.pause();
        if (producer?.track) producer.track.enabled = false;
        cameraWantedRef.current = false;
        setCameraEnabled(false);
        if (own) own.camera = false;
        setError("Модератор выключил вашу камеру");
      } else if (value.action === "allow_microphone") {
        setError("Модератор снова разрешил включить микрофон");
      } else if (value.action === "allow_camera") {
        setError("Модератор снова разрешил включить камеру");
      }
      renderPeers();
    };
    const removed = (value: { callId: string }) => {
      if (callRef.current?.callId !== value.callId) return;
      cleanup();
      setError("Модератор удалил вас из звонка");
    };
    const ended = (value: { callId: string }) => { if (callRef.current?.callId === value.callId) cleanup(); };
    socket.on("group-call:incoming", incoming); socket.on("group-call:active", active); socket.on("group-call:inactive", inactive);
    socket.on("group-call:participant-joined", joined); socket.on("group-call:participant-updated", participantUpdated); socket.on("group-call:participant-left", left); socket.on("group-call:producer-added", producerAdded); socket.on("group-call:producer-closed", producerClosed); socket.on("group-call:producer-paused", producerPaused); socket.on("group-call:producer-resumed", producerResumed);
    socket.on("group-call:active-speaker", activeSpeaker); socket.on("group-call:consumer-score", consumerScore); socket.on("group-call:producer-score", producerScore); socket.on("group-call:moderated", moderated); socket.on("group-call:removed", removed); socket.on("group-call:ended", ended);
    return () => { socket.off("group-call:incoming", incoming); socket.off("group-call:active", active); socket.off("group-call:inactive", inactive); socket.off("group-call:participant-joined", joined); socket.off("group-call:participant-updated", participantUpdated); socket.off("group-call:participant-left", left); socket.off("group-call:producer-added", producerAdded); socket.off("group-call:producer-closed", producerClosed); socket.off("group-call:producer-paused", producerPaused); socket.off("group-call:producer-resumed", producerResumed); socket.off("group-call:active-speaker", activeSpeaker); socket.off("group-call:consumer-score", consumerScore); socket.off("group-call:producer-score", producerScore); socket.off("group-call:moderated", moderated); socket.off("group-call:removed", removed); socket.off("group-call:ended", ended); };
  }, [cleanup, consumeProducer, ownUserId, renderPeers, socket, startRingtone, updateCall, updatePeerQuality]);

  useEffect(() => {
    if (!socket) return;
    const disconnected = () => { if (callRef.current?.status === "active") scheduleRecovery(); };
    const connected = () => { if (callRef.current?.status === "active" && connectionState !== "connected") scheduleRecovery(true); };
    socket.on("disconnect", disconnected);
    socket.on("connect", connected);
    return () => { socket.off("disconnect", disconnected); socket.off("connect", connected); };
  }, [connectionState, scheduleRecovery, socket]);

  useEffect(() => {
    const incomingFromPush = (event: Event) => {
      const data = (event as CustomEvent<Record<string, string>>).detail ?? {};
      if (data.type !== "incoming_call" || data.group !== "true" || !data.callId || !data.chatId || !data.fromUserId) return;
      const keyEpoch = Number(data.keyEpoch);
      if (!Number.isInteger(keyEpoch) || keyEpoch < 1) return;
      const base: Omit<GroupCallInfo, "status"> = {
        callId: data.callId,
        chatId: data.chatId,
        fromUserId: data.fromUserId,
        ...(data.callerName ? { callerName: data.callerName } : {}),
        ...(data.groupName ? { groupName: data.groupName } : {}),
        video: data.video === "true",
        e2eeMode: data.e2eeMode === "disabled" ? "disabled" : "required",
        keyEpoch,
        startedAt: data.startedAt || new Date().toISOString(),
      };
      setActiveCalls((current) => ({ ...current, [base.chatId]: { ...base, status: "incoming" } }));
      if (!callRef.current) updateCall({ ...base, status: "incoming" });
    };
    window.addEventListener("jtuc:native-push", incomingFromPush as EventListener);
    return () => window.removeEventListener("jtuc:native-push", incomingFromPush as EventListener);
  }, [updateCall]);

  useEffect(() => {
    if (call?.status === "active") postNativeMessage({ type: "call:active", callId: call.callId, video: call.video });
  }, [call]);

  useEffect(() => {
    const answer = (event: Event) => {
      const detail = (event as CustomEvent<{ actionId?: string | null; callId?: string; group?: boolean }>).detail;
      if (detail?.group === false) return;
      const callId = detail?.callId;
      if (!callId) return;
      const intent = { callId, actionId: detail?.actionId };
      pendingNativeAnswerRef.current = intent;
      processPendingNativeAnswer();
    };
    const end = (event: Event) => {
      const detail = (event as CustomEvent<{ actionId?: string | null; callId?: string; group?: boolean }>).detail;
      if (detail?.group === false || !detail?.callId) return;
      const intent = { callId: detail.callId, actionId: detail.actionId };
      if (callRef.current?.callId !== detail.callId) {
        pendingNativeDeclineRef.current = intent;
        return;
      }
      pendingNativeDeclineRef.current = null;
      declineCall();
      if (intent.actionId) postNativeMessage({ type: "call:action-ack", actionId: intent.actionId });
    };
    window.addEventListener("jtuc:native-call-answer", answer);
    window.addEventListener("jtuc:native-call-end", end);
    return () => { window.removeEventListener("jtuc:native-call-answer", answer); window.removeEventListener("jtuc:native-call-end", end); };
  }, [declineCall, processPendingNativeAnswer]);

  useEffect(() => {
    processPendingNativeAnswer();
  }, [call, processPendingNativeAnswer, socket?.connected]);

  useEffect(() => {
    const timer = window.setInterval(processPendingNativeAnswer, 500);
    const resume = () => processPendingNativeAnswer();
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [processPendingNativeAnswer]);

  useEffect(() => {
    const pending = pendingNativeDeclineRef.current;
    if (!call || call.status !== "incoming" || pending?.callId !== call.callId) return;
    pendingNativeDeclineRef.current = null;
    declineCall();
    if (pending.actionId) postNativeMessage({ type: "call:action-ack", actionId: pending.actionId });
  }, [call, declineCall]);

  useEffect(() => {
    if (!call || call.status !== "active" || !cameraEnabled) return;
    const timer = window.setInterval(() => {
      const track = producersRef.current.get("camera")?.track;
      if (!track || track.readyState === "ended") { void recoverCameraTrack(); return; }
      if (!track.muted) { cameraMutedAtRef.current = null; return; }
      cameraMutedAtRef.current ??= Date.now();
      if (Date.now() - cameraMutedAtRef.current >= 4_000) void recoverCameraTrack();
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [call, cameraEnabled, recoverCameraTrack]);

  useEffect(() => () => cleanup(), [cleanup]);

  return {
    call, activeCalls, participants, activeSpeakerId, muted, cameraEnabled, screenEnabled, mediaAction, connectionState, handRaised, error, devices, localStream,
    clearError: () => setError(null),
    e2eeSupported: encodedTransformSupported(), startCall, acceptCall, joinActiveCall, declineCall, leaveCall, endCall,
    toggleMute, toggleCamera, toggleScreen, toggleHandRaised, moderateParticipant, switchDevice, refreshDevices,
  };
}
